import React, { useEffect, useRef, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import HttpMethodDropdown from '../../buttons/HttpMethodDropdown';
import { useIsMount } from '../../../hooks/useIsMountHook';
import _ from 'lodash';
import { isCurlCommand, parseCurlToHttpRequest } from '../../../utils/sandboxUtils';
import { confirm } from '../../ui/modal/BlazeConfirmationModal';
import { HttpRequestDefinition } from '../SandBox.types';
import { getErrorMessage } from '../../../utils/miscUtil';
import { showToastNotification } from '../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../store/ui/toasts/toastsTypes';
import { useDispatch } from 'react-redux';

interface HttpRequestInputProps {
  isReadOnly?: boolean;
  method: string;
  path: string;
  onUpdate: (field: 'method' | 'path', value: any) => void;
  onImportHttpRequest: (httpRequest: HttpRequestDefinition) => void;
  className?: string;
}

const HttpRequestInput = ({
  onUpdate,
  method,
  isReadOnly,
  path,
  className,
  onImportHttpRequest,
}: HttpRequestInputProps) => {
  const [urlPath, setUrlPath] = useState(path);
  const isMount = useIsMount();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isMount && urlPath !== path) {
      updateUrlDebounced(urlPath);
    }
  }, [urlPath]);

  useEffect(() => {
    if (!isMount && path !== urlPath) {
      setUrlPath(path);
    }
  }, [path]);

  const onPasteHandler = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardText = await navigator.clipboard.readText();
    if (isCurlCommand(clipboardText)) {
      e.preventDefault();
      confirm({
        confirmation: (
          <div>
            <p>We noticed a cURL command. Do you want to transform it into an HTTP request for easier use?</p>
            <code className={'bg-light p-4 overflow-auto text-black d-block'} style={{ maxHeight: 450 }}>
              {clipboardText}
            </code>
          </div>
        ),
        yesOptionVariant: 'primary',
        yesOptionTitle: 'Import',
        title: 'Import cURL Command as HTTP Request',
      }).then(() => {
        try {
          const parsedRequest = parseCurlToHttpRequest(clipboardText, true);
          onImportHttpRequest(parsedRequest);
          dispatch(
            showToastNotification(
              ToastType.Success,
              'Request created',
              'The request has been successfully created from the cURL command.',
            ),
          );
        } catch (e) {
          const msg = getErrorMessage(e);
          dispatch(showToastNotification(ToastType.Danger, 'Error parsing cURL command', msg));
        }
      });
    }
  };

  const updateUrlDebounced = useRef(
    _.debounce((urlPath: string) => {
      onUpdate('path', urlPath);
    }, 600),
  ).current;

  return (
    <div className={className}>
      <label className={'pb-2'} htmlFor="url-path">
        URL:
      </label>
      <InputGroup>
        <HttpMethodDropdown
          isReadOnly={isReadOnly}
          method={method}
          updateState={(_field, value) => {
            onUpdate('method', value);
          }}
        />
        <Form.Control
          id={'url-path'}
          type={'text'}
          placeholder={'URL'}
          value={urlPath || ''}
          autoComplete={'off'}
          onChange={e => setUrlPath(e.target.value)}
          onPaste={e => {
            onPasteHandler(e);
          }}
        />
      </InputGroup>
    </div>
  );
};

export default HttpRequestInput;
